import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Trophy,
  Target,
  Users,
  Brain,
  Dumbbell,
  MapPin,
  HeartPulse,
  Salad,
  Award,
  ShieldCheck,
} from "lucide-react";
import { ACADEMY, PILLARS } from "../lib/academy-data";
import SectionHeader from "../components/SectionHeader";

const ICONS = { Target, Brain, Dumbbell };

const CLUB_PHOTOS = {
  senior:
    "https://customer-assets.emergentagent.com/job_hapoel-court-hub/artifacts/yrgg9k40_285A0219.jpg",
  action:
    "https://customer-assets.emergentagent.com/job_hapoel-court-hub/artifacts/t6yz1i8p_HA019531.jpg",
  huddle:
    "https://customer-assets.emergentagent.com/job_hapoel-court-hub/artifacts/txqliyr2_4E4A0588.jpg",
  trophy1:
    "https://customer-assets.emergentagent.com/job_hapoel-court-hub/artifacts/dhcycprp_HA019622.jpg",
  trophy2:
    "https://customer-assets.emergentagent.com/job_hapoel-court-hub/artifacts/f8cq9vh2_HA019660.jpg",
};

const ELITE_TEAMS = [
  { name: "ילדים לאומית", level: "Elite Youth U14", icon: Trophy },
  { name: "נערים לאומית", level: "Elite Cadets U16", icon: Trophy },
  { name: "נוער לאומית", level: "Elite Juniors U18", icon: Trophy },
];

const SENIOR_SUPPORT = [
  { icon: Dumbbell, label: "מאמן כושר" },
  { icon: Salad, label: "תזונאית" },
  { icon: Brain, label: "פסיכולוג ספורט" },
  { icon: HeartPulse, label: "פיזיותרפיה" },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[95vh] flex items-center overflow-hidden" data-testid="hero">
        <img
          src={CLUB_PHOTOS.action}
          alt="כדורסל הפועל מטה יהודה"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/85 to-[#0A0A0A]/55 z-10" />
        <div className="absolute inset-0 bg-gradient-to-l from-[#0A0A0A]/60 via-transparent to-[#0A0A0A]/60 z-10" />
        <div className="grain-overlay z-10" />

        <div className="container mx-auto px-6 md:px-10 relative z-20 py-24">
          <div className="max-w-5xl mx-auto text-center stagger">
            <div className="flex items-center justify-center gap-3 mb-7">
              <span className="block w-10 h-[3px] bg-[#DC2626]" />
              <p className="font-heading text-xs md:text-sm font-bold tracking-[0.4em] text-[#DC2626] uppercase">
                Hapoel Mate Yehuda Basketball Academy
              </p>
              <span className="block w-10 h-[3px] bg-[#DC2626]" />
            </div>

            {/* Main title — prominent academy name */}
            <h1 className="font-heading font-black text-white uppercase tracking-tight leading-[0.85]">
              <span className="block text-[#DC2626] text-6xl md:text-8xl lg:text-[10rem]">
                האקדמיה
              </span>
              <span className="block text-4xl md:text-6xl lg:text-7xl mt-2 md:mt-4">
                הפועל מטה יהודה
              </span>
            </h1>

            <p className="font-body text-lg md:text-2xl text-neutral-200 mt-8 leading-relaxed max-w-3xl mx-auto">
              מסלול מקצועי לבנים ובנות מגיל 14 ומעלה — אימון פיזי, מנטלי וטכני ברמה הגבוהה בארץ.
              <br />
              ליווי צמוד של <span className="text-white font-bold">{ACADEMY.leadProfessional}</span>.
            </p>

            <div className="flex flex-wrap gap-4 justify-center mt-10">
              <Link
                to="/program"
                className="inline-flex items-center justify-center gap-2 bg-[#DC2626] hover:bg-[#B91C1C] text-white font-heading font-bold text-base md:text-lg uppercase tracking-wider px-8 py-4 rounded-sm transition-all duration-300 shadow-[0_0_30px_rgba(220,38,38,0.55)] hover:shadow-[0_0_45px_rgba(220,38,38,0.8)] hover:-translate-y-0.5"
                data-testid="hero-program-btn"
              >
                גלו את התכנית
                <ArrowLeft size={20} strokeWidth={3} />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white hover:bg-white hover:text-[#0A0A0A] text-white font-heading font-bold text-base md:text-lg uppercase tracking-wider px-8 py-4 rounded-sm transition-all duration-300"
                data-testid="hero-contact-btn"
              >
                צור קשר
              </Link>
            </div>

            {/* Quick stats centered */}
            <div className="grid grid-cols-3 gap-4 md:gap-12 mt-16 max-w-3xl mx-auto">
              {[
                { value: "+20", label: "קבוצות במועדון" },
                { value: "14+", label: "גיל מינימום" },
                { value: "3", label: "קודקודי המשולש" },
              ].map((s) => (
                <div
                  key={s.label}
                  className="border-t-2 border-[#DC2626] pt-3"
                  data-testid={`hero-stat-${s.label}`}
                >
                  <div className="font-heading text-3xl md:text-5xl lg:text-6xl font-black text-white">
                    {s.value}
                  </div>
                  <div className="font-body text-xs md:text-sm text-neutral-400 uppercase tracking-wider mt-1">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* INTRO STRIP */}
      <section className="bg-[#0A0A0A] border-y border-white/10 py-8" data-testid="intro-strip">
        <div className="container mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12 text-right">
            <div className="flex items-center gap-4 justify-center md:justify-start">
              <MapPin size={32} strokeWidth={2.5} className="text-[#DC2626] flex-shrink-0" />
              <div>
                <div className="font-heading font-bold text-white">מיקום</div>
                <div className="font-body text-sm text-neutral-400">{ACADEMY.location}</div>
              </div>
            </div>
            <div className="flex items-center gap-4 justify-center md:justify-start">
              <Users size={32} strokeWidth={2.5} className="text-[#DC2626] flex-shrink-0" />
              <div>
                <div className="font-heading font-bold text-white">קהל יעד</div>
                <div className="font-body text-sm text-neutral-400">{ACADEMY.audience}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CLUB SECTION */}
      <section className="py-24 md:py-32 bg-[#050505] relative overflow-hidden" data-testid="club-section">
        <div className="absolute inset-0 diagonal-stripe opacity-30 pointer-events-none" />
        <div className="container mx-auto px-6 md:px-10 relative">
          <div className="max-w-3xl mx-auto text-center mb-14 md:mb-20">
            <p className="font-heading text-xs md:text-sm font-bold tracking-[0.3em] text-[#DC2626] uppercase mb-4">
              על המועדון
            </p>
            <h2 className="font-heading text-5xl md:text-6xl lg:text-7xl font-black text-white uppercase tracking-tight leading-[0.9]">
              הפועל
              <br />
              <span className="text-[#DC2626]">מטה יהודה</span>
            </h2>
            <p className="font-body text-lg md:text-xl text-neutral-300 mt-6 leading-relaxed">
              מועדון הכדורסל של הפועל מטה יהודה מונה <strong className="text-white">כ-20 קבוצות ליגה</strong> — ממחלקת הילדים ועד הבוגרים. שחקני ושחקניות האקדמיה ישתלבו בקבוצות העילית של המועדון.
            </p>
          </div>

          {/* Senior Team Spotlight - HIGHLIGHTED */}
          <div className="bg-gradient-to-br from-[#DC2626] to-[#7F1D1D] relative overflow-hidden p-8 md:p-12 lg:p-16 mb-16" data-testid="senior-team-spotlight">
            <div className="absolute inset-0 diagonal-stripe opacity-20 pointer-events-none" />
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center relative">
              <div className="lg:col-span-6 order-2 lg:order-1">
                <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur border border-white/30 px-4 py-2 mb-5">
                  <ShieldCheck size={16} strokeWidth={3} className="text-white" />
                  <span className="font-heading text-xs font-bold tracking-widest text-white uppercase">
                    הקבוצה הבכירה
                  </span>
                </div>
                <h3 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black text-white uppercase tracking-tight leading-[0.95]">
                  בוגרים
                  <br />
                  <span className="text-white/90">בליגה ארצית</span>
                </h3>
                <p className="font-body text-base md:text-lg text-white/95 mt-5 leading-relaxed">
                  למועדון <strong>קבוצת בוגרים בליגה ארצית</strong> שלה יש את <strong>כל המעטפת המקצועית</strong> הדרושה לספורטאי תחרותי ברמה גבוהה — בדיוק הסביבה שאליה השחקנים הצעירים שלנו שואפים להגיע.
                </p>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-8">
                  {SENIOR_SUPPORT.map((s, i) => (
                    <div
                      key={s.label}
                      className="bg-black/30 backdrop-blur border border-white/20 p-4 text-center"
                      data-testid={`senior-support-${i}`}
                    >
                      <s.icon size={26} strokeWidth={2.5} className="text-white mx-auto mb-2" />
                      <p className="font-heading text-xs md:text-sm font-bold text-white uppercase tracking-wide">
                        {s.label}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="lg:col-span-6 order-1 lg:order-2 relative">
                <div className="relative aspect-[4/3] overflow-hidden border-4 border-white/30">
                  <img
                    src={CLUB_PHOTOS.senior}
                    alt="קבוצת הבוגרים הפועל מטה יהודה"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-4 -right-4 bg-black text-white px-4 py-2 font-heading font-black text-xs md:text-sm tracking-widest uppercase border border-white/20">
                  קבוצה בכירה • ליגה ארצית
                </div>
              </div>
            </div>
          </div>

          {/* Elite Teams (where academy players will play) */}
          <div className="mb-12">
            <div className="flex items-center justify-center gap-3 mb-3">
              <span className="block w-10 h-[2px] bg-[#DC2626]" />
              <p className="font-heading text-xs md:text-sm font-bold tracking-[0.3em] text-[#DC2626] uppercase">
                לאן השחקנים שלנו משתלבים
              </p>
              <span className="block w-10 h-[2px] bg-[#DC2626]" />
            </div>
            <h3 className="font-heading text-3xl md:text-4xl lg:text-5xl font-black text-white uppercase tracking-tight text-center mb-10">
              קבוצות העלית של המועדון
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {ELITE_TEAMS.map((t, i) => (
                <div
                  key={t.name}
                  className="group bg-[#171717] border border-white/10 hover:border-[#DC2626] transition-all duration-300 p-8 text-center relative overflow-hidden"
                  data-testid={`elite-team-${i}`}
                >
                  <div className="absolute top-0 right-0 left-0 h-1 bg-[#DC2626] scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-right" />
                  <div className="w-16 h-16 mx-auto inline-flex items-center justify-center bg-[#DC2626]/15 border border-[#DC2626]/30 text-[#DC2626] mb-5">
                    <t.icon size={28} strokeWidth={2.5} />
                  </div>
                  <h4 className="font-heading text-2xl md:text-3xl font-black text-white uppercase tracking-tight">
                    {t.name}
                  </h4>
                  <p className="font-body text-xs text-neutral-500 uppercase tracking-[0.2em] mt-2" dir="ltr">
                    {t.level}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Action gallery */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4" data-testid="club-gallery">
            <div className="col-span-2 md:col-span-2 aspect-[4/3] overflow-hidden bg-black">
              <img
                src={CLUB_PHOTOS.huddle}
                alt="קבוצת הנוער"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
              />
            </div>
            <div className="aspect-square overflow-hidden bg-black">
              <img
                src={CLUB_PHOTOS.trophy1}
                alt="זכייה באליפות"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
              />
            </div>
            <div className="aspect-square overflow-hidden bg-black">
              <img
                src={CLUB_PHOTOS.trophy2}
                alt="קבוצת אלופים"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
              />
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]" data-testid="pillars-section">
        <div className="container mx-auto px-6 md:px-10">
          <SectionHeader
            kicker="המעטפת המקצועית"
            title="שלושת קודקודי המשולש"
            description="האקדמיה מספקת מעטפת מלאה בשלושה היבטים מרכזיים — הפיזי, המנטלי והטכני — כדי לפתח שחקנים שלמים."
            testId="pillars-header"
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PILLARS.map((pillar, idx) => {
              const Icon = ICONS[pillar.icon];
              return (
                <div
                  key={pillar.title}
                  className="group bg-[#171717] border border-white/10 hover:border-[#DC2626]/60 transition-all duration-500 p-8 md:p-10 relative overflow-hidden"
                  data-testid={`pillar-card-${idx}`}
                >
                  <div className="absolute top-0 left-0 w-1 h-0 group-hover:h-full bg-[#DC2626] transition-all duration-500" />
                  <div className="w-14 h-14 inline-flex items-center justify-center bg-[#DC2626]/15 border border-[#DC2626]/30 text-[#DC2626] mb-6 rounded-sm">
                    <Icon size={28} strokeWidth={2.5} />
                  </div>
                  <h3 className="font-heading text-2xl md:text-3xl font-black text-white mb-4 uppercase">
                    {pillar.title}
                  </h3>
                  <p className="font-body text-neutral-400 leading-relaxed mb-6">
                    {pillar.description}
                  </p>
                  <ul className="space-y-2">
                    {pillar.points.map((p) => (
                      <li
                        key={p}
                        className="font-body text-sm text-neutral-300 flex items-start gap-2"
                      >
                        <span className="w-1.5 h-1.5 bg-[#DC2626] mt-2 flex-shrink-0" />
                        {p}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>

          <div className="mt-12 text-center">
            <Link
              to="/program"
              className="inline-flex items-center gap-2 font-heading font-bold text-sm uppercase tracking-wider text-[#DC2626] hover:text-white transition-colors"
              data-testid="pillars-more-link"
            >
              קראו עוד על התכנית המלאה
              <ArrowLeft size={18} strokeWidth={3} />
            </Link>
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="py-24 md:py-32 bg-[#050505]" data-testid="why-section">
        <div className="container mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="relative">
            <div className="absolute -top-4 -right-4 w-full h-full border-2 border-[#DC2626]" />
            <img
              src={CLUB_PHOTOS.trophy2}
              alt="אלופים"
              className="relative w-full h-[460px] object-cover"
            />
          </div>
          <div>
            <p className="font-heading text-xs md:text-sm font-bold tracking-[0.3em] text-[#DC2626] uppercase mb-3">
              למה האקדמיה שלנו
            </p>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black text-white uppercase tracking-tight leading-[0.95]">
              חולמים להגיע
              <br />
              <span className="text-[#DC2626]">הכי רחוק שאפשר?</span>
            </h2>
            <p className="font-body text-base md:text-lg text-neutral-300 mt-6 leading-relaxed">
              זוהי ההזדמנות שלכם להצטרף למסלול שמיועד לשחקנים ושחקניות ממוקדי מטרה.
              בליווי מקצועי צמוד של <span className="text-white font-bold">{ACADEMY.leadProfessional}</span>,
              ובהובלת <span className="text-white font-bold">{ACADEMY.manager}</span> — מנהל מקצועי בהפועל מטה יהודה
              ועוזר מאמן מכבי אשדוד בליגת העל.
            </p>

            <div className="mt-8 space-y-4">
              {[
                { icon: Trophy, text: "אימונים מקצועיים ברמה הגבוהה בארץ" },
                { icon: Target, text: "תכנית אישית לשיפור הפן הטכני" },
                { icon: Award, text: "השתלבות בקבוצות העילית של המועדון" },
                { icon: Brain, text: "ליווי מנטלי, תזונתי וגופני — שחקנים שלמים" },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-4">
                  <div className="w-11 h-11 inline-flex items-center justify-center bg-[#DC2626] text-white rounded-sm flex-shrink-0">
                    <item.icon size={20} strokeWidth={3} />
                  </div>
                  <p className="font-body text-base md:text-lg text-white">{item.text}</p>
                </div>
              ))}
            </div>

            <Link
              to="/about"
              className="inline-flex items-center gap-2 mt-10 font-heading font-bold text-sm uppercase tracking-wider text-white hover:text-[#DC2626] transition-colors border-b-2 border-[#DC2626] pb-1"
              data-testid="why-more-link"
            >
              לעוד פרטים על האקדמיה
              <ArrowLeft size={18} strokeWidth={3} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
